import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadCloud } from 'lucide-react';

import { cn } from '@/lib/utils';
import { MAX_MEDIA_ASSET_SIZE_MB } from '@/hooks/use-media-upload';

interface IMediaUploadDropzoneProps {
    readonly accept: string;
    readonly disabled?: boolean;
    /**
     * `compact` trades the roomy `p-8` for `p-4` and a smaller glyph. Three of
     * these stack in the chat-widget wizard's ~300px column (see the
     * `@container` note in `widget-setup-wizard.tsx`), where the default
     * density renders ~540px of dropzone before a single label. The bug-report
     * drawer uses the same density for the same reason.
     */
    readonly density?: 'default' | 'compact';
    readonly onFilesSelected: (files: FileList) => void;
    /** Overrides the default "Drag an image here…" line. */
    readonly title?: string;
    /** Extra classes on the zone itself — mainly to tighten the padding. */
    readonly className?: string;
    /**
     * Overrides the default format/size line. Callers with a different limit
     * MUST pass this — the default quotes the media-library cap, which is not
     * universal (bug-report attachments allow 25 MB and non-image types).
     */
    readonly hint?: string;
}

/** Pure drag-drop + click-to-browse zone. Upload orchestration lives with the
 *  caller (the media-library dialog, the lead-form design step's
 *  `ImageSourceField`) — this component only surfaces the picked `FileList`. */
export function MediaUploadDropzone({
    accept,
    disabled,
    density = 'default',
    onFilesSelected,
    title,
    hint,
    className,
}: IMediaUploadDropzoneProps) {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const isCompact = density === 'compact';

    function openPicker() {
        if (!disabled) inputRef.current?.click();
    }

    function handleDrop(e: DragEvent<HTMLDivElement>) {
        e.preventDefault();
        setIsDragOver(false);
        if (disabled) return;
        if (e.dataTransfer.files.length > 0) onFilesSelected(e.dataTransfer.files);
    }

    function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
        }
    }

    return (
        <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={openPicker}
            onKeyDown={handleKeyDown}
            onDragOver={(e) => {
                e.preventDefault();
                if (!disabled) setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            className={cn(
                'flex flex-1 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border-default text-center transition-colors focus-visible:ring-3 focus-visible:ring-primary-200 focus-visible:outline-none',
                isCompact ? 'gap-1.5 p-4' : 'gap-2 p-8',
                isDragOver && 'border-primary-400 bg-primary-50 dark:bg-primary-900/10',
                disabled && 'pointer-events-none cursor-not-allowed opacity-50',
                className,
            )}
        >
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                disabled={disabled}
                onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0)
                        onFilesSelected(e.target.files);
                    e.target.value = '';
                }}
            />
            <UploadCloud className={cn('text-text-tertiary', isCompact ? 'size-6' : 'size-8')} />
            <p className={cn('font-medium text-text-primary', isCompact ? 'text-xs' : 'text-sm')}>
                {title ?? t('media.dropzone.title', 'Drag an image here, or click to browse')}
            </p>
            <p className={cn('text-text-tertiary', isCompact ? 'text-2xs' : 'text-xs')}>
                {hint ??
                    t('media.dropzone.hint', 'PNG, JPEG, GIF, or WEBP — up to {{size}} MB', {
                        size: MAX_MEDIA_ASSET_SIZE_MB,
                    })}
            </p>
        </div>
    );
}
